/**
 * Данные эпик-вью: дерево детей, их состояние и что происходило в GitLab.
 *
 * Дети берутся тем же фильтром по родителю, что и подзадачи карточки, — двумя
 * уровнями: истории эпика и подзадачи этих историй. Глубже иерархия не бывает
 * по правилам типов, поэтому рекурсии нет, а внуки грузятся параллельно, а не
 * по одному на строку.
 */
import { listActions, toReleases, toStands, type Release, type Stand } from '@/features/devops/api'
import { listChildren } from '@/features/issue/links'
import { listIssues } from '@/features/issues/api'
import { emptyFilters } from '@/features/issues/filters'
import { getSummary, type SummaryRow } from '@/features/pipelines/api'

import type { IssueRow } from '@/features/issues/api'
import type { ProjectColumn } from '@/features/projects/api'

/** Узел дерева: ребёнок эпика и его подзадачи */
export interface EpicNode {
  issue: IssueRow
  children: IssueRow[]
}

/** Подзадачи бывают только у того, кто сам может быть родителем */
const HAS_CHILDREN = ['ISSUE_TYPE_STORY', 'ISSUE_TYPE_TASK', 'ISSUE_TYPE_BUG']

export const loadTree = async (projectId: string, epicId: string): Promise<EpicNode[]> => {
  const level = await listChildren(projectId, epicId)

  return Promise.all(
    level.map(async (issue) => ({
      issue,
      children: HAS_CHILDREN.includes(issue.type) ? await listChildren(projectId, issue.id) : [],
    })),
  )
}

/** Все задачи эпика плоским списком — по ним считается прогресс */
export const flatten = (nodes: EpicNode[]): IssueRow[] =>
  nodes.flatMap((node) => [node.issue, ...node.children])

/** Разбивка задач эпика по категориям статусов — сегменты полосы прогресса */
export interface Progress {
  total: number
  done: number
  inProgress: number
  open: number
}

/**
 * Считаем по категории колонки, а не по её названию: «Готово» в одном проекте
 * зовётся «Выкачено», и сравнение строк врало бы у всех, кто переименовал
 * колонку.
 */
export const toProgress = (issues: IssueRow[], columns: ProjectColumn[]): Progress => {
  const categoryOf = (issue: IssueRow) =>
    columns.find((column) => column.id === issue.columnId)?.statusCategory ?? ''

  const done = issues.filter((issue) => categoryOf(issue) === 'STATUS_CATEGORY_DONE').length
  const inProgress = issues.filter(
    (issue) => categoryOf(issue) === 'STATUS_CATEGORY_IN_PROGRESS',
  ).length

  return { total: issues.length, done, inProgress, open: issues.length - done - inProgress }
}

/**
 * Последний прогон на каждый сервис эпика. Сводка отдаёт по одному прогону на
 * пару «сервис и ветка» — здесь сворачиваем до сервиса: на эпик смотрят, чтобы
 * понять, где красное, а не читать все ветки.
 */
export const byService = (rows: SummaryRow[]): SummaryRow[] => {
  const latest = new Map<string, SummaryRow>()

  for (const row of rows) {
    const current = latest.get(row.run.projectPath)

    if (!current) {
      latest.set(row.run.projectPath, row)

      continue
    }

    // Упавшее держим до последнего: сервис с красным прогоном остаётся
    // красным, даже если следом прошёл зелёный прогон другой ветки.
    if (current.run.status === 'failed') continue

    const newer = new Date(row.run.createdAt).getTime() > new Date(current.run.createdAt).getTime()

    if (row.run.status === 'failed' || newer) latest.set(row.run.projectPath, row)
  }

  return [...latest.values()]
}

export const loadPipelines = async (projectId: string, epicId: string): Promise<SummaryRow[]> =>
  byService(await getSummary(projectId, epicId))

/** Стенды и релизы эпика: журнал знает задачу каждой операции */
export interface EpicOps {
  stands: Stand[]
  releases: Release[]
}

export const loadOps = async (issueIds: string[]): Promise<EpicOps> => {
  const own = new Set(issueIds)
  const actions = (await listActions()).filter((action) => own.has(action.issueId))

  return { stands: toStands(actions), releases: toReleases(actions).slice(0, 3) }
}

/** Строка экрана эпиков: сам эпик и готовность по его задачам */
export interface EpicRow {
  issue: IssueRow
  progress: Progress
}

/** Сколько задач приезжает за раз */
const PAGE_SIZE = 100

/**
 * Потолок на проект. Дальше считать готовность по всем задачам разом дороже,
 * чем польза от точного числа: в проекте на тысячу задач эпик всё равно
 * открывают по одному.
 */
const MAX_ISSUES = 1000

const KEY_ORDER = { field: 'key', desc: false } as const

/**
 * Все задачи проекта. Первая страница приезжает сама, остальные — разом:
 * их число известно из `total`, и ждать их по очереди значит умножать
 * задержку на число страниц.
 */
const listAllIssues = async (projectId: string): Promise<IssueRow[]> => {
  const first = await listIssues(projectId, emptyFilters(), KEY_ORDER, 1, PAGE_SIZE)
  const pages = Math.min(Math.ceil(first.total / PAGE_SIZE), MAX_ISSUES / PAGE_SIZE)

  const rest = await Promise.all(
    Array.from({ length: Math.max(0, pages - 1) }, (_, index) =>
      listIssues(projectId, emptyFilters(), KEY_ORDER, index + 2, PAGE_SIZE),
    ),
  )

  return [...first.issues, ...rest.flatMap((page) => page.issues)]
}

/**
 * Эпики проекта с готовностью каждого.
 *
 * Считается по одной выдаче всех задач, а не обходом детей на каждый эпик:
 * иерархия в трекере трёхуровневая, и обход стоил бы запроса на эпик плюс
 * запроса на каждого его ребёнка — под сотню на экран, который открывают,
 * чтобы окинуть проект взглядом.
 *
 * Готовность — та же `toProgress`, что в разборе одного эпика: числа на двух
 * экранах обязаны совпадать, а два способа их посчитать рано или поздно
 * разъезжаются.
 */
export const loadEpics = async (
  projectId: string,
  columns: ProjectColumn[],
): Promise<EpicRow[]> => {
  const all = await listAllIssues(projectId)

  const byParent = new Map<string, IssueRow[]>()

  for (const issue of all) {
    if (!issue.parentId) continue

    byParent.set(issue.parentId, [...(byParent.get(issue.parentId) ?? []), issue])
  }

  // Дети и внуки: глубже иерархия не бывает по правилам типов.
  const descendants = (id: string): IssueRow[] =>
    (byParent.get(id) ?? []).flatMap((child) => [child, ...(byParent.get(child.id) ?? [])])

  const isDone = (issue: IssueRow) =>
    columns.find((column) => column.id === issue.columnId)?.statusCategory ===
    'STATUS_CATEGORY_DONE'

  return all
    .filter((issue) => issue.type === 'ISSUE_TYPE_EPIC')
    .map((issue) => ({ issue, progress: toProgress(descendants(issue.id), columns) }))
    // Закрытые эпики уходят вниз: экран открывают ради того, что в работе.
    .sort((left, right) => Number(isDone(left.issue)) - Number(isDone(right.issue)))
}
